import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { registerLocale } from "react-datepicker";
import de from 'date-fns/locale/de';
import theme from '../theme';
import config from '../config';
import { useAuth } from '../AuthContext';

registerLocale('de', de);

const BookingContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const SectionTitle = styled.h3`
  font-size: 20px;
  color: ${props => props.theme.colors.primary};
  margin: 0;
  font-weight: 600;
`;

const DatePickerContainer = styled.div`
  .react-datepicker {
    font-family: inherit;
    border: 2px solid ${props => props.theme.colors.accent1};
    border-radius: ${props => props.theme.borderRadius.medium};
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  }

  .react-datepicker__header {
    background-color: ${props => props.theme.colors.secondary};
    color: white;
    border-bottom: none;
    border-radius: ${props => props.theme.borderRadius.medium} ${props => props.theme.borderRadius.medium} 0 0;
  }

  .react-datepicker__current-month {
    color: white;
    font-weight: 600;
  }

  .react-datepicker__day-name {
    color: white;
  }

  .react-datepicker__day {
    border-radius: 50%;
    margin: 2px;
    transition: all 0.2s ease;
  }

  .react-datepicker__day:hover {
    background-color: ${props => props.theme.colors.accent1} !important;
    color: ${props => props.theme.colors.primary} !important;
  }

  .react-datepicker__day--selected {
    background-color: ${props => props.theme.colors.secondary} !important;
    color: white !important;
  }

  .react-datepicker__day--keyboard-selected {
    background-color: ${props => props.theme.colors.accent1} !important;
    color: ${props => props.theme.colors.primary} !important;
  }

  .react-datepicker__day--disabled {
    color: #ccc !important;
  }
`;

const DatePickerButton = styled.button`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid ${props => props.theme.colors.accent1};
  border-radius: ${props => props.theme.borderRadius.medium};
  font-size: 16px;
  background-color: white;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;
  color: ${props => props.selectedDate ? props.theme.colors.primary : '#666'};
  font-weight: ${props => props.selectedDate ? '600' : '400'};

  &:hover {
    background-color: ${props => props.theme.colors.accent1};
    color: ${props => props.theme.colors.primary};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  &:active {
    transform: translateY(0);
  }

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.secondary};
    box-shadow: 0 0 0 3px rgba(219, 39, 98, 0.1);
  }
`;

const DatePickerWrapper = styled.div`
  position: relative;
`;

const CustomDatePicker = styled(DatePicker)`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 1000;
  margin-top: 4px;
`;

const SlotsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 12px;
  margin-top: 10px;
`;

const SlotButton = styled.button`
  padding: 12px 16px;
  border: 2px solid ${props => props.available ? props.theme.colors.accent1 : '#e0e0e0'};
  border-radius: ${props => props.theme.borderRadius.medium};
  background-color: ${props => props.selected ? props.theme.colors.secondary : 'white'};
  color: ${props => {
    if (props.selected) return 'white';
    if (props.available) return props.theme.colors.primary;
    return '#999';
  }};
  font-size: 14px;
  font-weight: 600;
  cursor: ${props => props.available ? 'pointer' : 'not-allowed'};
  transition: all 0.2s ease;
  opacity: ${props => props.available ? 1 : 0.6};

  &:hover {
    ${props => props.available && `
      background-color: ${props.theme.colors.accent1};
      color: ${props.theme.colors.primary};
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    `}
  }

  &:active {
    transform: translateY(0);
  }
`;

const LoadingText = styled.p`
  text-align: center;
  color: ${props => props.theme.colors.secondary};
  font-style: italic;
`;

const NoSlotsText = styled.p`
  text-align: center;
  color: ${props => props.theme.colors.secondary};
  font-style: italic;
`;

const ActionButton = styled.button`
  margin: 32px auto 0 auto;
  display: block;

  padding: 14px 32px;
  background: linear-gradient(135deg, ${props => props.theme.colors.secondary} 0%, #e8316d 100%);
  color: white;
  border: none;
  border-radius: ${props => props.theme.borderRadius.medium};
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  &:hover {
    background: linear-gradient(135deg, #e8316d 0%, ${props => props.theme.colors.secondary} 100%);
    transform: scale(1.01);
    box-shadow: 0 6px 20px rgba(219, 39, 98, 0.4);
  }
`;

const LessonTypeSelector = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const LessonTypeDropdown = styled.select`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid ${props => props.theme.colors.accent1};
  border-radius: ${props => props.theme.borderRadius.medium};
  font-size: 16px;
  background-color: white;
  cursor: pointer;
  transition: all 0.2s ease;
  color: ${props => props.theme.colors.primary};
  font-weight: 500;

  &:hover {
    background-color: ${props => props.theme.colors.accent1};
    color: ${props => props.theme.colors.primary};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.secondary};
    box-shadow: 0 0 0 3px rgba(219, 39, 98, 0.1);
  }

  option {
    padding: 8px;
    font-size: 14px;
  }
`;

const BudgetInfo = styled.div`
  font-size: 12px;
  color: #666;
  margin-top: 4px;
  font-style: italic;
`;

const ErrorMessage = styled.div`
  background: #ffebee;
  color: #c62828;
  border: 1px solid #ffcdd2;
  border-radius: ${props => props.theme.borderRadius.medium};
  padding: 12px;
  margin-bottom: 16px;
  font-size: 14px;
`;

const SuccessMessage = styled.div`
  background: #e8f5e8;
  color: #2e7d32;
  border: 1px solid #4CAF50;
  border-radius: ${props => props.theme.borderRadius.medium};
  padding: 12px;
  margin-bottom: 16px;
  font-size: 14px;
`;

const LessonBooking = ({ onDatePickerFocus, onDatePickerBlur, onClose, onLessonBooked, availableBudgets = [], isRescheduling = false, lessonId = null, currentLesson = null, isAdmin = false, viewOnly = false }) => {
  const { token, isAuthenticated } = useAuth();
  const [selectedDate, setSelectedDate] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [selectedLessonType, setSelectedLessonType] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectableDates, setSelectableDates] = useState([]);
  const [loadingDates, setLoadingDates] = useState(true);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [lessonTypes, setLessonTypes] = useState([]);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [additionalInfo, setAdditionalInfo] = useState('');
  const [reschedulingReason, setReschedulingReason] = useState('');
  const [bookedLessonDetails, setBookedLessonDetails] = useState(null);

  // Filter lesson types based on available budgets (only for new bookings, not rescheduling or view-only)
  const availableLessonTypes = (isRescheduling || viewOnly)
    ? lessonTypes // When rescheduling or view-only, use all lesson types
    : lessonTypes.filter(lessonType => {
        return availableBudgets.some(budget => 
          budget.type === lessonType.name && parseInt(budget.amount) > 0
        );
      });

  // Auto-select lesson type when lesson types are loaded
  useEffect(() => {
    if (availableLessonTypes.length > 0 && !selectedLessonType) {
      let lessonTypeToSelect;
      
      if (isRescheduling && currentLesson) {
        // When rescheduling, try to find the current lesson's type
        lessonTypeToSelect = availableLessonTypes.find(lt => lt.id === currentLesson.lessonTypeId);
      }
      
      // If no specific lesson type found (or not rescheduling), use the first available
      if (!lessonTypeToSelect) {
        lessonTypeToSelect = availableLessonTypes[0];
      }
      
      setSelectedLessonType(lessonTypeToSelect);
      // Fetch selectable dates for the selected lesson type
      fetchSelectableDates(lessonTypeToSelect);
    }
  }, [availableLessonTypes, selectedLessonType, isRescheduling, currentLesson]);

  // Preselect additional info when rescheduling (but not for admins)
  useEffect(() => {
    if (isRescheduling && currentLesson && currentLesson.additionalInfo && !isAdmin) {
      setAdditionalInfo(currentLesson.additionalInfo);
    }
  }, [isRescheduling, currentLesson, isAdmin]);

  // Fetch selectable dates when lesson type is selected
  const fetchSelectableDates = async (lessonType) => {
    if (!isAuthenticated() || !token || !lessonType) return;

    setLoadingDates(true);
    try {
      const response = await fetch(config.getApiUrl('/api/lessons/selectable-dates'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          lessonType: lessonType.id
        })
      });

      if (response.ok) {
        const data = await response.json();
        setSelectableDates(data.selectableDates || []);
      } else {
        console.error('Failed to fetch selectable dates');
        setSelectableDates([]);
      }
    } catch (error) {
      console.error('Error fetching selectable dates:', error);
      setSelectableDates([]);
    } finally {
      setLoadingDates(false);
    }
  };

  // Fetch lesson types and selectable dates on component mount
  useEffect(() => {
    const fetchInitialData = async () => {
      if (!isAuthenticated() || !token) return;

      try {
        // Fetch lesson types
        const lessonTypesResponse = await fetch(config.getApiUrl('/api/lesson-types'), {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (lessonTypesResponse.ok) {
          const lessonTypesData = await lessonTypesResponse.json();
          setLessonTypes(lessonTypesData.lessonTypes || []);
        }

        // Selectable dates will be fetched when a lesson type is selected
      } catch (error) {
        console.error('Error fetching initial data:', error);
        setSelectableDates([]);
      } finally {
        setLoadingDates(false);
      }
    };

    fetchInitialData();
  }, [token, isAuthenticated]);

  const fetchAvailableSlots = async (date) => {
    if (!date || !selectedLessonType) return;
    
    setLoading(true);
    try {
      const adjustedDate = new Date(date);
      adjustedDate.setDate(adjustedDate.getDate() + 1);
      const dateString = adjustedDate.toISOString().split('T')[0];
      
      const response = await fetch(config.getApiUrl(`/api/lessons/slots/${dateString}?lessonType=${selectedLessonType.id}`), {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const slots = await response.json();
        setAvailableSlots(slots);
      } else {
        console.error('Failed to fetch slots');
        setAvailableSlots([]);
      }
    } catch (error) {
      console.error('Error fetching slots:', error);
      setAvailableSlots([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
    setSelectedSlot(null);
    setIsDatePickerOpen(false);
    if (selectedLessonType) {
      fetchAvailableSlots(date);
    }
  };

  const handleButtonClick = () => {
    if (!loadingDates) {
      setIsDatePickerOpen(!isDatePickerOpen);
      if (!isDatePickerOpen) {
        onDatePickerFocus?.();
      } else {
        onDatePickerBlur?.();
      }
    }
  };

  const handleSlotSelect = (slot) => {
    if (slot.available) {
      setSelectedSlot(slot);
    }
  };

  const handleLessonTypeSelect = (lessonType) => {
    setSelectedLessonType(lessonType);
    setSelectedSlot(null);
    setAvailableSlots([]);
    setSelectedDate(null); // Reset the picked date when lesson type changes
    // Fetch selectable dates for the selected lesson type
    if (lessonType) {
      fetchSelectableDates(lessonType);
    }
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('de-DE', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const filterDate = (date) => {
    if (loadingDates) return false;
    
    const adjustedDate = new Date(date);
    adjustedDate.setDate(adjustedDate.getDate() + 1);
    const dateString = adjustedDate.toISOString().split('T')[0];
    
    return selectableDates.includes(dateString);
  };

  const handleSubmit = async () => {
    if (!selectedDate || !selectedSlot || !selectedLessonType) {
      setSubmitStatus({ type: 'error', message: 'Bitte wählen Sie ein Datum, eine Zeit und einen Stundentyp aus.' });
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const adjustedDate = new Date(selectedDate);
      adjustedDate.setDate(adjustedDate.getDate() + 1);
      const dateString = adjustedDate.toISOString().split('T')[0];
      
      if (isRescheduling) {
        // Handle rescheduling - use admin endpoint for admins
        const endpoint = isAdmin 
          ? `/api/meyspage/lessons/${lessonId}/reschedule`
          : `/api/lessons/${lessonId}/reschedule`;
          
        const response = await fetch(config.getApiUrl(endpoint), {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            date: dateString,
            startTime: selectedSlot.startTime,
            endTime: selectedSlot.endTime,
            lessonTypeId: selectedLessonType.id,
            ...(isRescheduling ? { reschedulingReason: reschedulingReason.trim() } : { additionalInfo: additionalInfo.trim() })
          })
        });

        if (response.ok) {
          setSubmitStatus({ type: 'success', message: 'success' });
          
          // Call the callback to notify parent component
          if (onLessonBooked) {
            onLessonBooked({
              date: dateString,
              startTime: selectedSlot.startTime,
              endTime: selectedSlot.endTime,
              lessonTypeId: selectedLessonType.id,
              ...(isRescheduling ? { reschedulingReason: reschedulingReason.trim() } : { additionalInfo: additionalInfo.trim() })
            });
          }
          
          // Reset form
          setSelectedDate(null);
          setSelectedSlot(null);
          setSelectedLessonType(null);
          setAvailableSlots([]);
          setAdditionalInfo('');
          setReschedulingReason('');
        } else {
          const errorData = await response.json();
          setSubmitStatus({ type: 'error', message: errorData.message || 'Fehler beim Verschieben der Stunde.' });
        }
      } else {
        // Handle normal booking
        const response = await fetch(config.getApiUrl('/api/lessons/book'), {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            date: dateString,
            startTime: selectedSlot.startTime,
            endTime: selectedSlot.endTime,
            lessonTypeId: selectedLessonType.id,
            additionalInfo: additionalInfo.trim()
          })
        });

        if (response.ok) {
          // Store booking details before resetting form
          setBookedLessonDetails({
            date: selectedDate,
            slot: selectedSlot,
            lessonType: selectedLessonType
          });
          setSubmitStatus({ type: 'success', message: 'success' });
          
          // Call the callback to notify parent component
          if (onLessonBooked) {
            onLessonBooked(selectedLessonType.name);
          }
          
          // Reset form
          setSelectedDate(null);
          setSelectedSlot(null);
          setSelectedLessonType(null);
          setAvailableSlots([]);
          setAdditionalInfo('');
          setReschedulingReason('');
        } else {
          const errorData = await response.json();
          setSubmitStatus({ type: 'error', message: errorData.message || 'Fehler beim Buchen der Stunde.' });
        }
      }
    } catch (error) {
      console.error('Error booking/rescheduling lesson:', error);
      setSubmitStatus({ type: 'error', message: isRescheduling ? 'Netzwerkfehler beim Verschieben der Stunde.' : 'Netzwerkfehler beim Buchen der Stunde.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isAuthenticated()) {
    return (
      <BookingContainer>
        <ErrorMessage>
          Sie müssen angemeldet sein, um eine Stunde zu buchen.
        </ErrorMessage>
      </BookingContainer>
    );
  }

  return (
    <BookingContainer>
      {submitStatus && submitStatus.type === 'success' ? (
        <Section>
          <SectionTitle>Ausgewählte Stunde</SectionTitle>
          <p style={{
            fontSize: '18px',
            color: '#222',
            fontWeight: 500,
            textAlign: 'center',
            padding: '15px',
            backgroundColor: '#f5f5f5',
            borderRadius: theme.borderRadius.medium,
            margin: 0,
            border: `1px solid #e0e0e0`
          }}>
            {bookedLessonDetails && (
              <>
                {formatDate(bookedLessonDetails.date)} um {bookedLessonDetails.slot.startTime} - {bookedLessonDetails.slot.endTime}
              </>
            )}
          </p>
          <div style={{
            margin: '32px 0',
            padding: '24px',
            background: '#f5f5f5',
            borderRadius: theme.borderRadius.medium,
            border: '1px solid #e0e0e0',
            textAlign: 'center',
            fontSize: 18,
            color: theme.colors.primary,
            fontWeight: 500
          }}>
            Danke für deine Buchungsanfrage!<br />
            Wir prüfen deine Anfrage und bestätigen sie zeitnah.<br /><br />
            Du kannst dieses Fenster jetzt schließen.
          </div>
          
          <ActionButton
            style={{
              marginBottom: '-20px',
              marginTop: '0px',
              display: 'block',
              backgroundColor: theme.colors.secondary,
              color: 'white',
              border: 'none',
              borderRadius: theme.borderRadius.medium,
              fontSize: 18,
              fontWeight: 600,
              cursor: 'pointer'
            }}
            onClick={() => {
              if (typeof onClose === 'function') {
                onClose();
              } else if (typeof window !== 'undefined' && window.close) {
                window.close();
              }
            }}
          >
            Fenster schließen
          </ActionButton>
        </Section>
      ) : (
        <>
          {submitStatus && submitStatus.type === 'error' && (
            <Section>
              <ErrorMessage>{submitStatus.message}</ErrorMessage>
            </Section>
          )}

          {!isRescheduling && (
            <Section>
              <SectionTitle>Stundentyp auswählen</SectionTitle>
              <LessonTypeSelector>
                {availableLessonTypes.length > 0 ? (
                  <>
                    <LessonTypeDropdown
                      value={selectedLessonType?.id || ''}
                      onChange={(e) => {
                        const selectedId = e.target.value;
                        const lessonType = availableLessonTypes.find(lt => lt.id === selectedId);
                        handleLessonTypeSelect(lessonType);
                      }}
                    >
                      <option value="">Stundentyp auswählen...</option>
                      {availableLessonTypes.map((lessonType) => {
                        if (isRescheduling) {
                          // When rescheduling, just show the lesson type name
                          return (
                            <option key={lessonType.id} value={lessonType.id}>
                              {lessonType.name}
                            </option>
                          );
                        } else {
                          // When booking new, show budget information
                          const budget = availableBudgets.find(b => b.type === lessonType.name);
                          if (budget) {
                            return (
                              <option key={lessonType.id} value={lessonType.id}>
                                {lessonType.name} ({budget.amount} {parseInt(budget.amount) === 1 ? 'Stunde' : 'Stunden'} verfügbar)
                              </option>
                            );
                          } else {
                            // When no budget available (view-only mode), just show the lesson type name
                            return (
                              <option key={lessonType.id} value={lessonType.id}>
                                {lessonType.name}
                              </option>
                            );
                          }
                        }
                      })}
                    </LessonTypeDropdown>
                  </>
                ) : (
                  <div style={{
                    background: '#fff3cd',
                    color: '#856404',
                    border: '1px solid #ffeaa7',
                    borderRadius: theme.borderRadius.medium,
                    padding: '12px',
                    textAlign: 'center',
                    fontSize: '14px'
                  }}>
                    Keine Stundenpakete verfügbar. Bitte kaufe zuerst ein Stundenpaket.
                  </div>
                )}
              </LessonTypeSelector>
            </Section>
          )}

      {selectedLessonType && (
        <Section>
          <SectionTitle>Datum auswählen</SectionTitle>
          <DatePickerContainer>
            <DatePickerWrapper>
              <DatePickerButton
                onClick={handleButtonClick}
                disabled={loadingDates}
                selectedDate={selectedDate}
              >
                {loadingDates 
                  ? "Lade verfügbare Termine..." 
                  : selectedDate 
                    ? selectedDate.toLocaleDateString('de-DE', { 
                        day: '2-digit', 
                        month: '2-digit', 
                        year: 'numeric' 
                      })
                    : "Datum auswählen..."
                }
              </DatePickerButton>
              {isDatePickerOpen && (
                <CustomDatePicker
                  selected={selectedDate}
                  onChange={handleDateChange}
                  onBlur={() => {
                    setIsDatePickerOpen(false);
                    onDatePickerBlur?.();
                  }}
                  minDate={new Date()}
                  filterDate={filterDate}
                  dateFormat="dd.MM.yyyy"
                  locale="de"
                  inline
                  open={isDatePickerOpen}
                />
              )}
            </DatePickerWrapper>
          </DatePickerContainer>
        </Section>
      )}

      {selectedDate && selectedLessonType && (
        <Section>
          <SectionTitle>
            Verfügbare Zeiten für {formatDate(selectedDate)}
          </SectionTitle>
          {loading ? (
            <LoadingText>Lade verfügbare Zeiten...</LoadingText>
          ) : availableSlots.length > 0 ? (
            <SlotsGrid>
              {availableSlots.map((slot, index) => (
                <SlotButton
                  key={index}
                  available={slot.available}
                  selected={selectedSlot === slot}
                  onClick={() => handleSlotSelect(slot)}
                >
                  {slot.startTime} - {slot.endTime}
                </SlotButton>
              ))}
            </SlotsGrid>
          ) : (
            <NoSlotsText>Keine verfügbaren Zeiten für diesen Tag.</NoSlotsText>
          )}
        </Section>
      )}

      {selectedSlot && selectedLessonType && (
        <Section>
          <SectionTitle>Ausgewählte Stunde</SectionTitle>
          <p style={{ 
            fontSize: '18px', 
            color: '#222',
            fontWeight: 500,
            textAlign: 'center',
            padding: '15px',
            backgroundColor: '#f5f5f5',
            borderRadius: theme.borderRadius.medium,
            margin: 0,
            border: `1px solid #e0e0e0`
          }}>
            {formatDate(selectedDate)} um {selectedSlot.startTime} - {selectedSlot.endTime}
          </p>
          {!viewOnly && (
            <>
              <SectionTitle>
                {isRescheduling ? 'Verschiebungsgrund' : 'Zusätzliche Informationen (optional)'}
              </SectionTitle>
              <div>
                <textarea
                  value={isRescheduling ? reschedulingReason : additionalInfo}
                  onChange={(e) => {
                    if (isRescheduling) {
                      setReschedulingReason(e.target.value);
                    } else {
                      setAdditionalInfo(e.target.value);
                    }
                  }}
                  placeholder={
                    isRescheduling 
                      ? "Grund für die Verschiebung (optional)"
                      : "Falls du bestimmte Themen in dieser Stunde besprechen möchtest, schreibe sie gerne hier rein."
                  }
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: `2px solid ${theme.colors.accent1}`,
                    borderRadius: theme.borderRadius.medium,
                    fontSize: '16px',
                    fontFamily: 'inherit',
                    resize: 'vertical',
                    minHeight: '80px',
                    boxSizing: 'border-box',
                    fontFamily: 'inherit',
                  }}
                  maxLength={500}
                />
              </div>
            </>
          )}
          
          {!viewOnly && (
            <ActionButton
              onClick={handleSubmit}
              disabled={isSubmitting}
              style={{ marginBottom: '-20px' }}
            >
              {isSubmitting ? 'Bitte warten ...' : (isRescheduling ? 'Stunde verschieben' : 'Stunde buchen')}
            </ActionButton>
          )}
        </Section>
      )}
        </>
      )}
    </BookingContainer>
  );
};

export default LessonBooking; 